import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import type { MailboxInfo } from 'owa-client-types';
import {
    getAccountKeyForMailboxInfo,
    isSameCoprincipalAccountMailboxInfos,
} from 'owa-client-types';
import type MailFolderDragData from 'owa-mail-folder-store/lib/store/schema/MailFolderDragData';
import { lazyAddFavoriteFolder } from 'owa-mail-favorites-store';
import {
    getStore as getFavoritesStore,
    lazyUpdateFavoritePosition,
    isFolderInFavorites,
} from 'owa-favorites';
import type { FavoriteNodeDragData } from 'owa-favorites-types';
import { isCapabilitySupported } from 'owa-capabilities';
import { favoritesCapability } from 'owa-capabilities-definitions/lib/favoritesCapability';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list';
import { logUsage } from 'owa-analytics';
import {
    lazyMoveItemsBasedOnNodeIds,
    lazyCopyMailListRows,
    lazyMoveMailListRows,
    lazyAddCategoriesFromTable,
    lazyAddCategoriesFromItem,
} from 'owa-mail-triage-action';
import { setDragStateForAddinsView } from 'owa-dnd';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getStore } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import checkIfCrossAccountAndShowConfirmationDialogIfNeeded from 'owa-mail-triage-movecopy-importexport/lib/utils/checkIfCrossAccountAndShowConfirmationDialogIfNeeded';
import type { MailListItemPartDragData } from 'owa-mail-types/lib/types/MailListItemPartDragData';
import type { MailListRowDragData } from 'owa-mail-types/lib/types/MailListRowDragData';
import type { ActionSource } from 'owa-mail-store';

export const canDropFavorite = (dragData: DragData, mailboxInfo: MailboxInfo): boolean => {
    if (isMonarchMultipleAccountsEnabled()) {
        return true;
    }
    const draggedItemData = dragData.itemData as {
        mailboxInfo: MailboxInfo;
    };
    const favoriteMailboxInfo = draggedItemData?.mailboxInfo;
    return isSameCoprincipalAccountMailboxInfos(favoriteMailboxInfo, mailboxInfo);
};

export const onDropFavorite = async (
    dragData: DragData,
    favoriteId: string,
    actionSource?: ActionSource
): Promise<void> => {
    const draggedFavorite = dragData as FavoriteNodeDragData;
    const dropTargetId = favoriteId;
    const dragTargetId = draggedFavorite.favoriteId;
    const draggedItemData = dragData.itemData as {
        mailboxInfo: MailboxInfo;
    };
    const favoriteMailboxInfo = draggedItemData?.mailboxInfo;
    const updateFavoritePosition = await lazyUpdateFavoritePosition.import();
    updateFavoritePosition(
        dragTargetId /* idToUpdate */,
        dropTargetId /* destinationFolderId */,
        draggedFavorite.favoriteType,
        favoriteMailboxInfo /* mailboxInfo */
    );
    logUsage('FP_FavoritesDragAndDrop', favoriteMailboxInfo, {
        itemType: 'FavoriteNode',
        actionSource,
    });
};

// We are currently not:
//  * when combined favorites is not enabled, moving favorites from one section to another is not supported
//  * we shouldn't be adding folders that are already in favorites
export const canDropFolder = (dragData: DragData, mailboxInfo: MailboxInfo): boolean => {
    // If there is no information, return false
    if (!dragData.itemData) {
        return false;
    }
    const draggedItemData = dragData.itemData as {
        treeType: string;
        mailboxInfo: MailboxInfo;
        draggedFolderId: string;
    };

    if (!isCapabilitySupported(favoritesCapability, draggedItemData.mailboxInfo)) {
        return false;
    }

    // We should not be re-adding folders to favorites that are already there
    if (isFolderInFavorites(draggedItemData.draggedFolderId)) {
        return false;
    }

    if (isMonarchMultipleAccountsEnabled()) {
        return true;
    } else {
        // if combined favorites is not on, we should only be adding favorites that match the mailbox
        return isSameCoprincipalAccountMailboxInfos(draggedItemData.mailboxInfo, mailboxInfo);
    }
};

export const onDropFolder = async (
    dragData: DragData,
    mailboxInfo: MailboxInfo,
    favoriteId: string,
    actionSource?: ActionSource
): Promise<void> => {
    const mailFolderItemBeingDragged = dragData as MailFolderDragData;
    const draggedFolderId = mailFolderItemBeingDragged.folderId;
    const dropTargetId = favoriteId;
    let newIndex = undefined;
    if (isMonarchMultipleAccountsEnabled()) {
        newIndex = getFavoritesStore().orderedCombinedOutlookFavoritesIds.indexOf(dropTargetId);
    } else {
        const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
        newIndex = getFavoritesStore()
            .favoriteTreeData.get(accountKey)
            ?.orderedOutlookFavoritesIds.indexOf(dropTargetId);
    }

    const addFavoriteFolder = await lazyAddFavoriteFolder.import();
    addFavoriteFolder(draggedFolderId, 'Drag', newIndex);

    logUsage('FP_FavoritesDragAndDrop', mailboxInfo, {
        itemType: 'MailFolderNode',
        actionSource,
    });
};

export const onDropMailListItemPart = async (
    dragData: DragData,
    folderId: string,
    actionSource?: ActionSource
): Promise<void> => {
    const mailListItemPartDragData = dragData as MailListItemPartDragData;
    const tableView = getStore().tableViews.get(mailListItemPartDragData.tableViewId);
    if (!tableView) {
        return;
    }
    const moveItemsBasedOnNodeIds = await lazyMoveItemsBasedOnNodeIds.import();
    moveItemsBasedOnNodeIds(mailListItemPartDragData.nodeIds, folderId, tableView.id, 'Drag');
    logUsage('FP_FavoritesDragAndDrop', mailListItemPartDragData.mailboxInfos[0], {
        itemType: 'MailListItemPart',
        actionSource,
    });
};

export const onDropMailListRows = async (
    dragData: DragData,
    folderId: string,
    ctrlKey?: boolean,
    actionSource?: ActionSource
): Promise<void> => {
    const mailListRowDragData = dragData as MailListRowDragData;
    const tableViewId = mailListRowDragData.tableViewId;
    const rowKeys = mailListRowDragData.rowKeys;
    if (ctrlKey) {
        const tableView = getStore().tableViews.get(tableViewId);
        if (tableView) {
            lazyCopyMailListRows.importAndExecute(rowKeys, tableView, folderId, 'Drag');
        }
    } else {
        lazyMoveMailListRows.importAndExecute(rowKeys, folderId, tableViewId, 'Drag');
    }
    if (isGlobalFeatureEnabled('addin-dragAndDrop')) {
        setDragStateForAddinsView(false);
    }
    logUsage('FP_FavoritesDragAndDrop', mailListRowDragData.mailboxInfos[0], {
        itemType: 'MailListRow',
        actionSource,
    });
};

/**
 * Applies the favorited category to the dragged mail list rows. The category is added (not
 * replaced) so existing categories on the items are preserved, and conversation mode applies
 * the category to every item in the conversation via addCategoriesFromTable.
 * When the drag crosses accounts, the existing move-between-accounts confirmation dialog is
 * shown before the category is applied.
 * @param dragData the dragged mail list rows
 * @param categoryName the name of the favorited category to apply
 * @param categoryMailboxInfo the mailbox that owns the favorited category
 */
export const onDropMailListRowsToCategory = async (
    dragData: DragData,
    categoryName: string,
    categoryMailboxInfo: MailboxInfo
): Promise<void> => {
    const mailListRowDragData = dragData as MailListRowDragData;
    const tableView = getStore().tableViews.get(mailListRowDragData.tableViewId);
    if (!tableView || !categoryName) {
        return;
    }

    const sourceMailboxInfo = getMailboxInfo(tableView);
    const shouldProceed = await checkIfCrossAccountAndShowConfirmationDialogIfNeeded(
        'categorize',
        sourceMailboxInfo,
        categoryMailboxInfo
    );
    if (!shouldProceed) {
        return;
    }

    lazyAddCategoriesFromTable.importAndExecute(
        mailListRowDragData.rowKeys,
        tableView,
        [categoryName],
        'Drag'
    );
};

/**
 * Applies the favorited category to a dragged mail list item part (a single message dragged
 * from within an expanded conversation). The category is added so existing categories are
 * preserved. Cross-account drags are gated by the move-between-accounts confirmation dialog.
 * @param dragData the dragged mail list item part
 * @param categoryName the name of the favorited category to apply
 * @param categoryMailboxInfo the mailbox that owns the favorited category
 */
export const onDropMailListItemPartToCategory = async (
    dragData: DragData,
    categoryName: string,
    categoryMailboxInfo: MailboxInfo
): Promise<void> => {
    const mailListItemPartDragData = dragData as MailListItemPartDragData;
    const itemIds = mailListItemPartDragData.itemIds;
    const sourceMailboxInfo = mailListItemPartDragData.mailboxInfos[0];
    if (!itemIds?.length || !categoryName || !sourceMailboxInfo) {
        return;
    }

    const shouldProceed = await checkIfCrossAccountAndShowConfirmationDialogIfNeeded(
        'categorize',
        sourceMailboxInfo,
        categoryMailboxInfo
    );
    if (!shouldProceed) {
        return;
    }

    lazyAddCategoriesFromItem.importAndExecute(
        itemIds[0],
        sourceMailboxInfo,
        [categoryName],
        undefined,
        'Drag'
    );
};
